import { useState, type ReactNode, type Ref } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import ChevronRightOutlined from '@mui/icons-material/ChevronRightOutlined';
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined';
import CheckOutlined from '@mui/icons-material/CheckOutlined';

export interface JsonViewerProps {
  /**
   * The payload to render. Ingestion payloads arrive as a raw jsonb **string**;
   * an already-parsed object/array is also accepted. An unparseable string is
   * shown verbatim (never throws).
   */
  data: unknown;
  /** Header label. */
  title?: string;
  /** Copy button aria-label (i18n). Defaults to the English literal. */
  copyLabel?: string;
  copiedLabel?: string;
  /** Start collapsed. */
  collapsedDefault?: boolean;
  ref?: Ref<HTMLDivElement>;
}

interface SyntaxColors {
  key: string;
  str: string;
  num: string;
  bool: string;
  nul: string;
  punc: string;
}

// Polaris-tinted JSON syntax colors (kit `Display.jsx` JsonViewer), per mode.
const LIGHT: SyntaxColors = {
  key: '#004785',
  str: '#0D6F0D',
  num: '#C45E08',
  bool: '#793176',
  nul: '#697C8C',
  punc: '#8A96A3',
};
const DARK: SyntaxColors = {
  key: '#9BC6EB',
  str: '#7FD1A3',
  num: '#F8AD6D',
  bool: '#F89B45',
  nul: '#9AAAB8',
  punc: 'rgba(255,255,255,0.55)',
};

/** Pretty-print the payload; flag whether it is real JSON (false → show raw). */
function normalize(data: unknown): { text: string; isJson: boolean } {
  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (trimmed === '') return { text: '', isJson: false };
    try {
      return { text: JSON.stringify(JSON.parse(trimmed), null, 2), isJson: true };
    } catch {
      return { text: data, isJson: false };
    }
  }
  try {
    return { text: JSON.stringify(data ?? null, null, 2), isJson: true };
  } catch {
    return { text: String(data), isJson: false };
  }
}

function colorValue(raw: string, c: SyntaxColors): ReactNode {
  const v = raw.trim();
  if (/^".*",?$/.test(v)) return <span style={{ color: c.str }}>{raw}</span>;
  if (/^(true|false),?$/.test(v)) return <span style={{ color: c.bool }}>{raw}</span>;
  if (/^null,?$/.test(v)) return <span style={{ color: c.nul }}>{raw}</span>;
  if (/^-?\d/.test(v)) return <span style={{ color: c.num }}>{raw}</span>;
  return <span style={{ color: c.punc }}>{raw}</span>;
}

/**
 * Read-only syntax-tinted JSON block (monospace), ported from the prototype kit.
 * Collapsible header with a copy affordance. Theme-aware (light/dark syntax
 * ramps). Non-JSON input renders verbatim so it never throws on a raw payload.
 */
export function JsonViewer({
  data,
  title = 'Payload',
  copyLabel = 'Copy',
  copiedLabel = 'Copied',
  collapsedDefault = false,
  ref,
}: JsonViewerProps) {
  const theme = useTheme();
  const [open, setOpen] = useState(!collapsedDefault);
  const [copied, setCopied] = useState(false);
  const { text, isJson } = normalize(data);
  const c = theme.palette.mode === 'dark' ? DARK : LIGHT;

  const body: ReactNode = isJson
    ? text.split('\n').map((line, i) => {
        const m = line.match(/^(\s*)("(?:[^"\\]|\\.)*")(\s*:\s*)?(.*)$/);
        if (m && m[3]) {
          return (
            <div key={i}>
              {m[1]}
              <span style={{ color: c.key }}>{m[2]}</span>
              <span style={{ color: c.punc }}>{m[3]}</span>
              {colorValue(m[4] ?? '', c)}
            </div>
          );
        }
        return (
          <div key={i} style={{ color: c.punc }}>
            {line}
          </div>
        );
      })
    : text;

  const copy = () => {
    void navigator.clipboard?.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <Box
      ref={ref}
      sx={{
        bgcolor: theme.palette.mode === 'dark' ? '#0A1929' : '#FAFBFC',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '6px',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          p: '8px 12px',
          bgcolor: 'tableHeaderBg',
          borderBottom: open ? '1px solid' : 'none',
          borderColor: 'divider',
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => setOpen((o) => !o)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            bgcolor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            p: 0,
            fontFamily: (t) => t.typography.button.fontFamily,
            fontSize: 12,
            fontWeight: 600,
            color: 'text.primary',
          }}
        >
          {open ? (
            <ExpandMoreOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
          ) : (
            <ChevronRightOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
          )}
          {title}
          {isJson && (
            <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              · application/json
            </Box>
          )}
        </Box>
        <Box
          component="button"
          type="button"
          onClick={copy}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            bgcolor: 'transparent',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '4px',
            p: '4px 8px',
            cursor: 'pointer',
            fontFamily: (t) => t.typography.button.fontFamily,
            fontSize: 11,
            fontWeight: 500,
            color: 'text.secondary',
          }}
        >
          {copied ? (
            <CheckOutlined sx={{ fontSize: 13, color: 'success.main' }} />
          ) : (
            <ContentCopyOutlined sx={{ fontSize: 13, color: 'text.secondary' }} />
          )}
          {copied ? copiedLabel : copyLabel}
        </Box>
      </Box>
      {open && (
        <Box
          component="pre"
          sx={{
            m: 0,
            p: '12px 14px',
            overflowX: 'auto',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
            fontSize: 12.5,
            lineHeight: 1.65,
            color: 'text.primary',
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'pre',
          }}
        >
          {body}
        </Box>
      )}
    </Box>
  );
}
